import { useEffect, useRef, useState } from "react";
import { useGetQuizQuery } from "../api/artOfTheDayApi";
import { useGetMyCardsQuery } from "../../respectCards/api/respectCardsApi";
import type { RaceData } from "../api/types";
import type { UiState } from "../state/types";

/** How long the art waits for the hand and the quiz once it is decoded. */
export const SIDE_CAP_MS = 1500;
/** How long the page holds at all when the art never arrives. */
export const ART_CAP_MS = 8000;

/** Same test as AotdStats: the race graph and the quiz only show with rivals. */
function hasRivals(race: RaceData | undefined): boolean {
  return Boolean(race && race.author && race.participants && race.participants.length >= 1);
}

/**
 * Holds the AOTD page on the screen it was showing until today's art screen
 * can appear in one go: the image decoded, and the card hand (`/my-cards`)
 * and the guessing board (`/quiz`) answered. Before this the hand and the
 * board popped in around a grey placeholder. Starting both reads here also
 * means they load alongside the image instead of after the art card mounts;
 * they are the same cache entries the hand and the quiz read.
 *
 * The hold starts each time the page arrives at the completed screen and
 * ends for good once it is shown, so a refresh that swaps the image never
 * puts the page back on hold. A slow side read waits at most SIDE_CAP_MS
 * after the art, and an image that never comes waits ART_CAP_MS.
 *
 * Returns the UI state to render: the last shown one (the spinner on a fresh
 * load) while holding, otherwise `ui`.
 */
export function useAotdReveal(
  ui: UiState,
  opts: { isAuthenticated: boolean; authReady: boolean },
): UiState {
  const completed = ui.kind === "completed";
  const artUrl = ui.kind === "completed" ? ui.artUrl : null;
  const race = ui.kind === "completed" ? ui.art.race : undefined;

  const [decodedUrl, setDecodedUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!artUrl) return undefined;
    let live = true;
    const img = new Image();
    img.src = artUrl;
    // a broken image releases the page too; the <img> shows what it can
    const done = () => {
      if (live) setDecodedUrl(artUrl);
    };
    if (typeof img.decode === "function") img.decode().then(done, done);
    else img.onload = img.onerror = done;
    return () => {
      live = false;
    };
  }, [artUrl]);
  const artReady = artUrl !== null && decodedUrl === artUrl;

  const hand = useGetMyCardsQuery(undefined, { skip: !opts.isAuthenticated });
  const rivals = completed && hasRivals(race);
  const quiz = useGetQuizQuery(undefined, { skip: !opts.authReady || !rivals });
  const handSettled = !opts.isAuthenticated || hand.isSuccess || hand.isError;
  const quizSettled = !rivals || quiz.isSuccess || quiz.isError;
  const ready = artReady && opts.authReady && handSettled && quizSettled;

  const [released, setReleased] = useState(false);
  useEffect(() => {
    if (!completed) setReleased(false);
    else if (ready) setReleased(true);
  }, [completed, ready]);
  useEffect(() => {
    if (!completed || released) return undefined;
    const t = setTimeout(() => setReleased(true), artReady ? SIDE_CAP_MS : ART_CAP_MS);
    return () => clearTimeout(t);
  }, [completed, released, artReady]);

  const holding = completed && !released && !ready;
  const lastShown = useRef<UiState>({ kind: "loading" });
  useEffect(() => {
    if (!holding) lastShown.current = ui;
  });
  return holding ? lastShown.current : ui;
}
